import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { deployedAppsApi } from "@/lib/api";
// ============================================================
// Settings — pause/resume toggles for every deployed app the
// caller can see, grouped by source session. Root sees every
// app; non-root sees their own + orphans. Each app shows a
// state badge + a single up/down switch + an Open link +
// a Delete button. Deleting cascades the session-cleanup path
// (wipes the live and .stopped/ dirs).
//
// We re-fetch after every mutation rather than mutating local
// state; the toggle latency is sub-100ms and a stale UI is worse
// than a small roundtrip. The state badge colour comes from
// the backend's "state" field directly — no client-side guess.
// ============================================================
const STATE_META = {
    running: { dot: "bg-emerald-500", label: "Running", tone: "text-emerald-400" },
    stopped: { dot: "bg-zinc-500", label: "Paused", tone: "text-zinc-400" },
    starting: { dot: "bg-amber-500", label: "Starting", tone: "text-amber-400" },
    error: { dot: "bg-rose-500", label: "Error", tone: "text-rose-400" },
};
function StateBadge({ state }) {
    const meta = STATE_META[state] ?? STATE_META.stopped;
    return (_jsxs("span", { className: `inline-flex items-center gap-1.5 text-tx-xs font-medium ${meta.tone}`, children: [_jsx("span", { className: `inline-block size-2 rounded-full ${meta.dot}` }), meta.label] }));
}
function urlFor(slug) {
    // Match the apex ojas.karmacode.cloud → apps live at <slug>.<host>
    // The host is the apex with "ojas." stripped. Frontend knows this
    // because the chat strip uses the same host. We mirror that here.
    const host = window.location.hostname.replace(/^ojas\./, "");
    return `https://${slug}.${host}/`;
}
export default function Settings() {
    const [groups, setGroups] = useState(null);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(null); // slug being toggled
    const reload = useCallback(async () => {
        try {
            const data = await deployedAppsApi.mine();
            setGroups(data);
            setErr(null);
        }
        catch (e) {
            setErr(e?.message ?? "failed to load");
        }
    }, []);
    useEffect(() => { reload(); }, [reload]);
    const toggle = useCallback(async (app) => {
        setBusy(app.slug);
        setErr(null);
        try {
            if (app.state === "running") {
                await deployedAppsApi.stop(app.slug);
            }
            else if (app.state === "stopped" || app.state === "error") {
                await deployedAppsApi.start(app.slug);
            }
            else {
                // starting — wait, the user will retry
            }
            await reload();
        }
        catch (e) {
            setErr(e?.message ?? "toggle failed");
        }
        finally {
            setBusy(null);
        }
    }, [reload]);
    const remove = useCallback(async (app) => {
        if (!confirm(`Delete "${app.name}"? This wipes the app dir and frees the slug permanently.`)) {
            return;
        }
        setBusy(app.slug);
        setErr(null);
        try {
            await deployedAppsApi.delete(app.slug);
            await reload();
        }
        catch (e) {
            setErr(e?.message ?? "delete failed");
        }
        finally {
            setBusy(null);
        }
    }, [reload]);
    if (groups === null) {
        return (_jsx("div", { className: "mx-auto max-w-3xl p-6 text-muted", children: "Loading\u2026" }));
    }
    if (groups.length === 0) {
        return (_jsxs("div", { className: "mx-auto max-w-3xl space-y-3 p-6", children: [_jsx("h1", { className: "font-serif text-2xl font-semibold tracking-tight", children: "Settings" }), _jsxs("div", { className: "rounded-2xl border border-border bg-surface p-8 text-center", children: [_jsx("p", { className: "text-tx-sm text-muted", children: "No deployed apps yet." }), _jsxs("p", { className: "mt-1 text-tx-xs text-subtle", children: ["Build something in a chat session, then click the ", _jsx("span", { className: "font-mono", children: "\uD83D\uDE80 Deploy" }), " button above the chat to publish it. The dialog asks for a slug and shows the project to deploy."] })] })] }));
    }
    return (_jsxs("div", { className: "mx-auto max-w-3xl space-y-4 p-6", children: [_jsxs("header", { children: [_jsx("h1", { className: "font-serif text-2xl font-semibold tracking-tight", children: "Settings" }), _jsx("p", { className: "mt-1 text-tx-xs text-muted", children: "Pause an app to free its memory + CPU without losing the slug. Toggle it back on to restore the same URL." })] }), err && (_jsx("div", { className: "rounded border border-danger/30 bg-danger/10 px-3 py-2 text-tx-xs text-danger", children: err })), groups.map((g) => (_jsxs("section", { className: "rounded-2xl border border-border bg-surface p-4", children: [_jsxs("h2", { className: "mb-3 font-serif text-lg font-semibold tracking-tight", children: [g.session_name, g.session_id && (_jsx("span", { className: "ml-2 font-mono text-tx-xs text-subtle", children: g.session_id.slice(0, 8) }))] }), _jsx("ul", { className: "divide-y divide-border", children: g.deployed_apps.map((app) => {
                            const isBusy = busy === app.slug;
                            const isOff = app.state === "stopped" || app.state === "error";
                            return (_jsxs("li", { className: "flex flex-wrap items-center gap-3 py-3 sm:flex-nowrap", children: [_jsxs("div", { className: "min-w-0 flex-1", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("a", { href: urlFor(app.slug), target: "_blank", rel: "noopener noreferrer", className: "truncate font-mono text-tx-sm font-medium text-fg hover:underline", children: app.slug }), _jsx(StateBadge, { state: app.state })] }), _jsxs("p", { className: "mt-0.5 truncate text-tx-xs text-muted", children: [app.name, app.project_dir && (_jsxs("span", { className: "ml-1 text-subtle", children: ["\u00B7 ", _jsxs("span", { className: "font-mono", children: [app.project_dir, "/"] })] }))] })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { onClick: () => toggle(app), disabled: isBusy || app.state === "starting", "aria-label": isOff ? "Bring app online" : "Pause app", className: `relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full
                                  transition-colors disabled:opacity-50
                                  ${isOff ? "bg-zinc-600" : "bg-emerald-500"}`, children: _jsx("span", { className: `inline-block size-5 transform rounded-full bg-white shadow
                                    transition-transform
                                    ${isOff ? "translate-x-0.5" : "translate-x-5"}` }) }), _jsx("button", { onClick: () => remove(app), disabled: isBusy, className: "rounded border border-border bg-bg px-2 py-1 text-tx-xs text-muted hover:border-danger/40 hover:text-danger disabled:opacity-50", children: "Delete" })] })] }, app.slug));
                        }) })] }, g.session_id ?? "_orphan")))] }));
}
